import apiEndPoints from "../../apiconstants"
import {
  buildClaimIntakeRequestHeaders,
  shouldUseClaimIntakeRestPaths,
  toClaimIntakeApiPath,
} from "../../claimIntakeApiConfig";
import { api, handleErrorResponse } from "../api"
import type { RootState } from "../../store";
import { AgentViewPayload, ChatFeedbackResponse, ChatPreSignedURLResponse, ExpertAnswerPayload, ExpertAnswerResponse, FeedbackPayload, GetPdfLinkResponse, ImageGeneratePayload, ImageGenerateResponse, PresignedUrlPayload, SourceIds, AgentFeedbackPayload } from "./chatadvisortypes"



// Define a service using a base URL and expected endpoints
export const chatAdvisorHttpApi = api.injectEndpoints({
  endpoints: builder => ({
    getPdfLink: builder.mutation<GetPdfLinkResponse, SourceIds>({
      query: (getPdfLinkRequestPayload) => ({
        url: apiEndPoints?.getPdfLink, // Adjust URL if necessary
        method: 'POST',
        body: getPdfLinkRequestPayload,
      }),
      transformErrorResponse: handleErrorResponse
    })
    ,
    updateFeedback: builder.mutation<ChatFeedbackResponse, FeedbackPayload>({
      query: (payload) => ({
        url: apiEndPoints?.updateFeedback,
        method: 'PATCH',
        body: payload
      }),
      transformErrorResponse: handleErrorResponse
    }),
    updateGuestFeedback: builder.mutation<ChatFeedbackResponse, { payLoad: FeedbackPayload, token: { apiKey: string, apiToken: string } }>({
      query: ({ payLoad, token }) => ({
        url: apiEndPoints?.updateGustFeedback,
        method: 'PATCH',
        body: payLoad,
        headers: {
          ...(token?.apiToken && { Authorization: `Bearer ${token.apiToken}` }),
          ...(token?.apiKey && { 'x-api-key': token.apiKey }),
        },
      }),
      transformErrorResponse: handleErrorResponse
    }),
    updateAgentFeedback: builder.mutation<ChatFeedbackResponse, AgentFeedbackPayload>({
      queryFn: async (payload, api, _extra, baseQuery) => {
        const url = apiEndPoints?.updateAgentFeedback;
        const state = api.getState() as RootState;
        const useRest = shouldUseClaimIntakeRestPaths(state);
        const result = useRest
          ? await baseQuery({
              url: toClaimIntakeApiPath(url, true),
              method: 'PATCH',
              body: payload,
              headers: buildClaimIntakeRequestHeaders(api.getState),
            })
          : await baseQuery({
              url,
              method: 'PATCH',
              body: payload,
            });
        if (result.error) return { error: result.error };
        return { data: result.data as ChatFeedbackResponse };
      },
      transformErrorResponse: handleErrorResponse
    }),
    submitExpertAnswer: builder.mutation<ExpertAnswerResponse, ExpertAnswerPayload>({
      query: (reqPayload) => ({
        url: apiEndPoints?.expertAnswer,
        method: 'POST',
        body: {
          ...reqPayload,
        }
      }),
      transformErrorResponse: handleErrorResponse
    }),
    updateExpertAnswer: builder.mutation<ExpertAnswerResponse, ExpertAnswerPayload>({
      query: (reqPayload) => ({
        url: apiEndPoints?.expertAnswer,
        method: 'PATCH',
        body: {
          ...reqPayload,
        }
      }),
      transformErrorResponse: handleErrorResponse
    }),
    generateImage: builder.mutation<ImageGenerateResponse, ImageGeneratePayload>({
      queryFn: async (payload, api, _extra, baseQuery) => {
        const url = apiEndPoints?.getGenerateImage;
        const state = api.getState() as RootState;
        const useRest = shouldUseClaimIntakeRestPaths(state);
        const guestToken = state.advisor?.guestToken as
          | { apiToken?: string; apiKey?: string }
          | undefined;
        const result = useRest
          ? await baseQuery({
              url: url.replace('/api', '/rest'),
              method: 'POST',
              body: payload,
              headers: {
                ...(guestToken?.apiToken && { Authorization: `Bearer ${guestToken.apiToken}` }),
                ...(guestToken?.apiKey && { 'x-api-key': guestToken.apiKey }),
              },
            })
          : await baseQuery({
              url,
              method: 'POST',
              body: payload,
            });
        if (result.error) return { error: result.error };
        return { data: result.data as ImageGenerateResponse };
      },
      transformErrorResponse: handleErrorResponse
    }),
    getExpertAnswer: builder.query<ExpertAnswerResponse, string>({
      query: (source_id) => `${apiEndPoints?.expertAnswer}/${source_id}`,
    }),
    getExpertAnswerDetailsByMessageId: builder.query<any, { messageId: string, senderID: string }>({
      query: (props) => `${apiEndPoints?.getExpertAnswerByMsgId}?sender_id=${props?.senderID}&message_id=${props?.messageId}`,
      transformErrorResponse: handleErrorResponse,
      transformResponse: (response: any) => response.data
    }),
    getPdfLindkForConversations: builder.mutation<ChatPreSignedURLResponse, { payLoad: any, agentSource: boolean }>({
      query: ({ payLoad, agentSource }) => {
        console.log('payLoad', payLoad);

        return {
          url: agentSource ? apiEndPoints?.getAgentPresignedUrl : apiEndPoints?.getPdfLinkForConversations,
          method: 'POST',
          body: {
            ...payLoad,
          },
        }
      },
      transformErrorResponse: handleErrorResponse
    }),
    getPdfLinkForConversations: builder.mutation<ChatPreSignedURLResponse, PresignedUrlPayload>({
      query: (apiPayLoad) => ({
        url: apiEndPoints?.getPdfLinkForConversations, // Adjust URL if necessary
        method: 'POST',
        body: apiPayLoad,
      }),
      transformErrorResponse: handleErrorResponse
    }),
    getAgentKowledgeSource: builder.mutation<ChatPreSignedURLResponse, AgentViewPayload>({
      query: (apiPayLoad) => ({
        url: apiEndPoints?.getAgentPresignedUrl,
        method: 'POST',
        body: apiPayLoad,
      }),
      transformErrorResponse: handleErrorResponse
    }),
    getGuestPdfLinkForConversations: builder.mutation<ChatPreSignedURLResponse, any>({
      query: ({ token, ...payLoad }) => ({
        url: apiEndPoints?.getGuestPdfLinkForConversations,
        method: 'POST',
        body: payLoad,
        headers: {
          ...(token?.apiToken && { Authorization: `Bearer ${token.apiToken}` }),
          ...(token?.apiKey && { 'x-api-key': token.apiKey }),
        },
      }),
      transformErrorResponse: handleErrorResponse
    }),
    updateExpertAnswerStatus: builder.mutation<ExpertAnswerResponse, any>({
      query: (source_id) => ({
        url: apiEndPoints?.updateExpertAnswerStatus,
        method: 'PATCH',
        body: {
          ...source_id
        }
      }),
      transformErrorResponse: handleErrorResponse
    }),
    getExplainAnswerDetails: builder.mutation<any, string>({
      query: (message_id) => ({
        url: `${apiEndPoints?.getExplainAnswer}/${message_id}`,
        method: 'GET'
      }),
      transformResponse: (response: any) => response.data,
      transformErrorResponse: handleErrorResponse
    }),

  })
})

// Hooks are auto-generated by RTK-Query
// Same as `advisorApiSlice.endpoints.getQuotes.useQuery`
export const { useGetPdfLinkMutation, useUpdateFeedbackMutation, useUpdateGuestFeedbackMutation, useUpdateAgentFeedbackMutation, useSubmitExpertAnswerMutation, useUpdateExpertAnswerMutation, useGenerateImageMutation, useGetPdfLinkForConversationsMutation, useGetGuestPdfLinkForConversationsMutation, useGetExpertAnswerDetailsByMessageIdQuery, useGetExpertAnswerQuery, useUpdateExpertAnswerStatusMutation, useGetExplainAnswerDetailsMutation, useGetAgentKowledgeSourceMutation } = chatAdvisorHttpApi
